import { API } from "@swivl-mono/api-helper";
import { NLPEventsDaysCombined, QuestAnalyticsDaysCombinedInterface, ReviewGetType, ReviewInterface, UserAnalyticsDaysCombinedInterface } from "@swivl-mono/types";
import { ButtonColor, ButtonSize, ButtonWithOptions, Spinner } from "@swivl-mono/ui";
import React from "react";

import "../../../Components/BotAnalyticsDashboard/BotAnalyticsDashboard.scss";
import { HandledDataForType, QuestData, TopIntents, UserDataForType } from "../../../Components/BotAnalyticsDashboard/Helpers/DataHelpers";
import { BarAndPieChart, BarAndPieChartData } from "../../../Components/Charts/BarAndPieChart/BarAndPieChart";
import { StackedBarGraph, StackedBarGraphData } from "../../../Components/Charts/StackedBarGraph/StackedBarGraph";
import ViewAllReviews from "./ViewAllHelpers";

export async function fetchReviews(orgId: number, type: ReviewGetType): Promise<ReviewInterface[]> {
  try {
    const reviews = await API.Reviews.getReviews(orgId, type);
    return reviews;
  } catch (error) {
    console.error("Error fetching reviews:", error);
    return [];
  }
}

async function fetchLocationNames(orgId: number): Promise<{ [key: number]: string }> {
  try {
    const response = await API.Locations.getGroupsAndLocations(orgId); // API.LocationVars.getAllLocationVariables(orgId) instead
    const locations = response.flatMap((group) => group.locations);
    const locationMap = locations.reduce(
      (acc, location) => {
        acc[location.id] = `${location.name} (${location.id})`;
        return acc;
      },
      {} as { [key: number]: string },
    );
    return locationMap;
  } catch (error) {
    console.error("Error fetching location names:", error);
    return {};
  }
}

function transformRatingDataForChart(reviews: ReviewInterface[]): BarAndPieChartData[] {
  const ratingCounts = reviews.reduce(
    (acc, review) => {
      acc[review.rating] = (acc[review.rating] || 0) + 1;
      return acc;
    },
    {} as { [key: number]: number },
  );

  const chartData = Object.keys(ratingCounts).map((rating) => ({
    // title: `${rating} Star${Number(rating) > 1 ? 's' : ''}`,
    title: `${rating} ⭐️`,
    value: ratingCounts[rating],
  }));
  return chartData;
}

/* const ReviewCharts: React.FC = () => {
    const [reviews, setReviews] = useState<ReviewInterface[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const loadReviews = async () => {
            const orgId = Member.orgId!;
            const fetchedReviews = await fetchReviews(orgId, ReviewGetType.ALL);
            setReviews(fetchedReviews);
            setLoading(false);
        };

        loadReviews();
    }, []);

    if (loading) {
        return <Spinner />;
    }

    const ratingCounts = reviews.reduce((acc, review) => {
        acc[review.rating] = (acc[review.rating] || 0) + 1;
        return acc;
    }, {} as { [key: number]: number });
    const chartData = Object.keys(ratingCounts).map(rating => ({
        x: rating,
        y: ratingCounts[rating]
    }));

    const totalReviews = reviews.length;
    const averageRating = (reviews.reduce((acc, review) => acc + review.rating, 0) / totalReviews).toFixed(1);

    return (
        <div className="BotAnalyticsDashboard">
          <div className="chartBox">
            <div className="chartHeader">
              <div className="chartHeaderTitle">
                <span>Reviews</span>
              </div>
              <div className="spacer" />
              <div className="headerTotal">{totalReviews.toLocaleString()}</div>
            </div>
            <div className="chartContent">
              <div className="TotalsAndChart">
                <div className="totalsColumn">
                  <div className="total">
                    <div className="number">{totalReviews}</div>
                    <label>Total Reviews</label>
                  </div>
                  <div className="vs">
                    <div className="number" style={{ color: '#F67902' }}>{averageRating}</div>
                    <label>Average Rating</label>
                  </div>
                </div>
                <div className="stackedChart">
                  <VictoryChart
                    theme={VictoryTheme.material}
                    domainPadding={20}
                    containerComponent={<VictoryContainer responsive={false} />}
                  >
                    <VictoryAxis
                      tickValues={[1, 2, 3, 4, 5]}
                      tickFormat={['1 Star', '2 Stars', '3 Stars', '4 Stars', '5 Stars']}
                    />
                    <VictoryAxis
                      dependentAxis
                      tickFormat={(x) => `${x}`}
                    />
                    <VictoryBar
                      data={chartData}
                      x="x"
                      y="y"
                      style={{
                        data: { fill: '#F67902' } // Set the color to orangish
                      }}
                    />
                  </VictoryChart>
                </div>
              </div>
            </div>
            <div className="reviewsList">
              <ul>
                {reviews.map((review, index) => (
                  <li key={index}>
                    <p><strong>{review.reviewerName}</strong>: {review.text}</p>
                    <p>Rating: {review.rating}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      );
};

export default ReviewCharts; */

export enum MenuOption {
  Day = "Day",
  TimeOfDay = "TimeOfDay",
  DayOfWeek = "DayOfWeek",
}
function TitleForMenuOption(option: MenuOption): string {
  switch (option) {
    case MenuOption.Day:
      return "Day";
    case MenuOption.TimeOfDay:
      return "Time of Day";
    case MenuOption.DayOfWeek:
      return "Day of Week";
  }
  return "unknown";
}

function BlankState() {
  return <div className="AnalyticsBlankState">No Data to Show</div>;
}

interface ReviewData {
  byDateTime: {
    [key: string]: {
      total: number;
      positiveReviews: number;
      negativeReviews: number;
      dayOfWeek: string;
      byTimeOfDay: string;
      locationId: number;
      locationName: string;
      reviews: ReviewInterface[];
    };
  };
  updatedByDateTime: {
    [key: string]: {
      total: number;
      positiveReviews: number;
      negativeReviews: number;
      dayOfWeek: string;
      byTimeOfDay: string;
      locationId: number;
      locationName: string;
      reviews: ReviewInterface[];
    };
  };
}

interface Props {
  orgId: number;
  startDay: string;
  endDay: string;

  viewAllQuestsClicked?(): void;
  viewAllIntentsClicked?(): void;
  viewAllReviews?(): void;
}
interface State {
  automationMenu: MenuOption;
  nlpData?: NLPEventsDaysCombined;

  userMenu: MenuOption;
  userData?: UserAnalyticsDaysCombinedInterface;

  questData?: QuestAnalyticsDaysCombinedInterface;

  reviewMenu: MenuOption;
  reviewData?: ReviewData;
  showAllReviews?: boolean;

  updatedReviewMenu?: MenuOption;
  updatedReviewData?: ReviewData;
}

function AnalyticsEvent(category?: string, action?: string, label?: string, value?: string) {
  if ((window as any).AnalyticsEvent) {
    (window as any).AnalyticsEvent(category, action, label, value);
  }
}

export class BotAnalyticsDashboard extends React.Component<Props, State> {
  serverAddress = "https://swivl-analytics.herokuapp.com";
  locationNames: { [key: number]: string } = {};

  constructor(props: Props) {
    super(props);
    this.state = { automationMenu: MenuOption.Day, userMenu: MenuOption.TimeOfDay, reviewMenu: MenuOption.Day, updatedReviewMenu: MenuOption.Day };
    this.menuSelected = this.menuSelected.bind(this);
    this.loadData = this.loadData.bind(this);
    this.loadNLP = this.loadNLP.bind(this);
    this.loadUser = this.loadUser.bind(this);
    this.loadQuests = this.loadQuests.bind(this);
    this.loadReviews = this.loadReviews.bind(this);
  }
  async componentDidMount() {
    await this.loadLocationNames();
    this.loadData();
  }
  loadData() {
    this.loadNLP();
    this.loadUser();
    this.loadQuests();
    this.loadReviews();
  }
  menuSelected(e) {
    let split = e.target.name.split("_");
    if (split.length > 1) {
      let state = {};
      state[split[0]] = split[1];

      this.setState(state);

      if (split[0] == "automationMenu") {
        AnalyticsEvent("Dashboard", "Automations by", split[1]);
      }
      if (split[0] == "userMenu") {
        AnalyticsEvent("Dashboard", "Chats by", split[1]);
      }
      if (split[0] == "reviewMenu") {
        AnalyticsEvent("Dashboard", "Reviews by", split[1]);
      }
      if (split[0] == "updatedReviewMenu") {
        AnalyticsEvent("Dashboard", "Updated Reviews by", split[1]);
      }
    }
  }
  componentDidUpdate(nextProps: Props) {
    const { startDay, endDay, orgId } = this.props;
    if (nextProps.startDay !== startDay || nextProps.endDay !== endDay || nextProps.orgId !== orgId) {
      this.setState({ nlpData: undefined, userData: undefined, questData: undefined, reviewData: undefined, showAllReviews: false }, () => {
        this.loadData();
      });
    }
  }

  loadNLP() {
    fetch(`${this.serverAddress}/api/nlp/${this.props.orgId}/${this.props.startDay}/${this.props.endDay}`)
      .then((r) => r.json())
      .then((r) => {
        this.setState({ nlpData: r });
      });
  }
  loadUser() {
    fetch(`${this.serverAddress}/api/user/${this.props.orgId}/${this.props.startDay}/${this.props.endDay}`)
      .then((r) => r.json())
      .then((r) => {
        this.setState({ userData: r });
      });
  }

  loadQuests() {
    fetch(`${this.serverAddress}/api/quests/${this.props.orgId}/${this.props.startDay}/${this.props.endDay}`)
      .then((r) => r.json())
      .then((r) => {
        this.setState({ questData: r });
      });
  }

  async loadLocationNames() {
    this.locationNames = await fetchLocationNames(this.props.orgId);
  }

  async loadReviews() {
    try {
      const reviews = await fetchReviews(this.props.orgId, ReviewGetType.ALL);
      const transformedData = this.transformReviewData(reviews);
      this.setState({ reviewData: transformedData });
    } catch (error) {
      console.error("Error loading reviews:", error);
    }
  }

  transformReviewData(reviews: ReviewInterface[]) {
    const byDateTime = {};
    const updatedByDateTime = {};

    reviews.forEach((review) => {
      const reviewDate = new Date(review.reviewDate);
      if (reviewDate >= new Date(this.props.startDay) && reviewDate <= new Date(this.props.endDay)) {
        const day = reviewDate.toISOString().split("T")[0];
        const dayOfWeek = reviewDate.toLocaleString("en-US", { weekday: "long" });
        const hour = reviewDate.getHours();
        let minutes = reviewDate.getMinutes();
        if (minutes < 15) {
          minutes = 0;
        } else if (minutes < 45) {
          minutes = 30;
        } else {
          minutes = 0;
          reviewDate.setHours(hour + 1);
        }
        const byTimeOfDay = `${hour.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
        const locationName = this.locationNames[review.locationId] || "Unknown";

        if (!byDateTime[day]) {
          byDateTime[day] = {
            total: 0,
            positiveReviews: 0,
            negativeReviews: 0,
            dayOfWeek: dayOfWeek,
            byTimeOfDay: byTimeOfDay,
            locationId: review.locationId,
            locationName: locationName,
            reviews: [],
          };
        }
        byDateTime[day].total += 1;
        byDateTime[day].reviews.push(review);
        if (review.rating >= 4) {
          byDateTime[day].positiveReviews += 1;
        } else if (review.rating <= 3) {
          byDateTime[day].negativeReviews += 1;
        }
      }

      review.history.forEach((history) => {
        const historyDate = new Date(history.date);
        if (historyDate >= new Date(this.props.startDay) && historyDate <= new Date(this.props.endDay)) {
          const day = historyDate.toISOString().split("T")[0];
          const dayOfWeek = historyDate.toLocaleString("en-US", { weekday: "long" });
          const hour = historyDate.getHours();
          let minutes = historyDate.getMinutes();
          if (minutes < 15) {
            minutes = 0;
          } else if (minutes < 45) {
            minutes = 30;
          } else {
            minutes = 0;
            historyDate.setHours(hour + 1);
          }
          const byTimeOfDay = `${hour.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
          const locationName = this.locationNames[review.locationId] || "Unknown";

          if (!updatedByDateTime[day]) {
            updatedByDateTime[day] = {
              total: 0,
              positiveReviews: 0,
              negativeReviews: 0,
              dayOfWeek: dayOfWeek,
              byTimeOfDay: byTimeOfDay,
              locationId: review.locationId,
              locationName: locationName,
              reviews: [],
            };
          }
          updatedByDateTime[day].total += 1;
          updatedByDateTime[day].reviews.push(review);
          if (review.rating >= 4) {
            updatedByDateTime[day].positiveReviews += 1;
          } else if (review.rating <= 3) {
            updatedByDateTime[day].negativeReviews += 1;
          }
        }
      });
    });
    return { byDateTime, updatedByDateTime };
  }

  ReviewDataForType(data: ReviewData, type: MenuOption, isUpdated: boolean = false): StackedBarGraphData {
    let output: StackedBarGraphData = {
      ticks: [],
      segments: [[], []],
      colors: ["#F67902", "#FFA900"],
    };

    const dataSource = isUpdated ? data.updatedByDateTime : data.byDateTime;

    if (type === MenuOption.Day) {
      for (const day in dataSource) {
        if (Object.prototype.hasOwnProperty.call(dataSource, day)) {
          let dayDate = new Date(day + "T12:00:00.000-06:00");
          let stringVersion = String(dayDate.getMonth() + 1) + "/" + String(dayDate.getDate());
          output.ticks.push(stringVersion);

          output.segments[0].push({ x: stringVersion, y: dataSource[day].positiveReviews });
          output.segments[1].push({ x: stringVersion, y: dataSource[day].negativeReviews });
        }
      }
      output.tickCount = 3;
    }

    if (type === MenuOption.TimeOfDay) {
      const timeSlots = {};

      // Initialize all half-hour time slots from 12:00 AM to 11:59 PM
      for (let hour = 0; hour < 24; hour++) {
        for (let minutes of [0, 30]) {
          const timeOfDay = `${hour.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
          timeSlots[timeOfDay] = { total: 0, positiveReviews: 0, negativeReviews: 0 };
        }
      }

      for (const day in dataSource) {
        if (Object.prototype.hasOwnProperty.call(dataSource, day)) {
          const timeOfDay = dataSource[day].byTimeOfDay;
          if (timeSlots[timeOfDay]) {
            timeSlots[timeOfDay].total += dataSource[day].total;
            timeSlots[timeOfDay].positiveReviews += dataSource[day].positiveReviews;
            timeSlots[timeOfDay].negativeReviews += dataSource[day].negativeReviews;
          }
        }
      }

      const sortedTimeSlots = Object.keys(timeSlots).sort((a, b) => {
        const [aHour, aMinute] = a.split(":").map(Number);
        const [bHour, bMinute] = b.split(":").map(Number);
        return aHour * 60 + aMinute - (bHour * 60 + bMinute);
      });

      sortedTimeSlots.forEach((timeOfDay) => {
        output.ticks.push(timeOfDay);
        output.segments[0].push({ x: timeOfDay, y: timeSlots[timeOfDay].positiveReviews });
        output.segments[1].push({ x: timeOfDay, y: timeSlots[timeOfDay].negativeReviews });
      });

      output.tickCount = 7;
    }

    if (type === MenuOption.DayOfWeek) {
      const daysArray = [
        { day: "Mon", data: { total: 0, positiveReviews: 0, negativeReviews: 0 } },
        { day: "Tue", data: { total: 0, positiveReviews: 0, negativeReviews: 0 } },
        { day: "Wed", data: { total: 0, positiveReviews: 0, negativeReviews: 0 } },
        { day: "Thu", data: { total: 0, positiveReviews: 0, negativeReviews: 0 } },
        { day: "Fri", data: { total: 0, positiveReviews: 0, negativeReviews: 0 } },
        { day: "Sat", data: { total: 0, positiveReviews: 0, negativeReviews: 0 } },
        { day: "Sun", data: { total: 0, positiveReviews: 0, negativeReviews: 0 } },
      ];

      for (const day in dataSource) {
        if (Object.prototype.hasOwnProperty.call(dataSource, day)) {
          const dayOfWeek = dataSource[day].dayOfWeek;
          const dayData = daysArray.find((d) => d.day === dayOfWeek.substring(0, 3));
          if (dayData) {
            dayData.data.total += dataSource[day].total;
            dayData.data.positiveReviews += dataSource[day].positiveReviews;
            dayData.data.negativeReviews += dataSource[day].negativeReviews;
          }
        }
      }

      for (let i = 0; i < daysArray.length; i++) {
        const element = daysArray[i];
        output.ticks.push(element.day);
        output.segments[0].push({ x: element.day, y: element.data.positiveReviews });
        output.segments[1].push({ x: element.day, y: element.data.negativeReviews });
      }
      output.tickCount = 7;
    }

    return output;
  }

  renderReviews() {
    const { reviewData, reviewMenu } = this.state;
    let content;
    if (reviewData && reviewData.byDateTime) {
      if (Object.keys(reviewData.byDateTime).length === 0) {
        content = <div className="loadingContent">{BlankState()}</div>;
      } else {
        const data = this.ReviewDataForType(reviewData, reviewMenu);
        content = this.renderTotalsAndChart(
          Object.values(reviewData.byDateTime)
            .reduce((acc, day) => acc + day.total, 0)
            .toLocaleString(),
          Object.values(reviewData.byDateTime).reduce((acc, day) => acc + day.positiveReviews, 0),
          Object.values(reviewData.byDateTime).reduce((acc, day) => acc + day.negativeReviews, 0),
          "TOTAL",
          "POSITIVE",
          "NEGATIVE",
          ["#F67902", "#FFA900"],
          reviewMenu === MenuOption.TimeOfDay,
          data,
        );
      }
    } else {
      content = (
        <div className="loadingContent">
          <Spinner />
        </div>
      );
    }
    return (
      <div className="chartBox">
        <div className="chartHeader">
          <div className="chartHeaderTitle">
            <span>Reviews by </span>
            <ButtonWithOptions
              color={ButtonColor.WHITE}
              size={ButtonSize.SMALL}
              buttonOptions={[
                { title: "Day", name: "reviewMenu_Day", onClick: this.menuSelected },
                { title: "Time of Day", name: "reviewMenu_TimeOfDay", onClick: this.menuSelected },
                { title: "Day of Week", name: "reviewMenu_DayOfWeek", onClick: this.menuSelected },
              ]}
            >
              {TitleForMenuOption(reviewMenu)}
            </ButtonWithOptions>
          </div>
          <div className="spacer" />
          <div className="chartKey">
            <div className="chartKeyItem">
              <span className="chartKeyBox" style={{ background: "#F67902" }} />
              Positive
            </div>
            <div className="chartKeyItem">
              <span className="chartKeyBox" style={{ background: "#FFA900" }} />
              Negative
            </div>
          </div>
        </div>
        <div className="chartContent">{content}</div>
      </div>
    );
  }

  renderUpdatedReviews() {
    const { reviewData, updatedReviewMenu } = this.state;
    let content;
    if (reviewData && reviewData.updatedByDateTime) {
      if (Object.keys(reviewData.updatedByDateTime).length === 0) {
        content = <div className="loadingContent">{BlankState()}</div>;
      } else {
        const data = this.ReviewDataForType(reviewData, updatedReviewMenu || MenuOption.Day, true);
        content = this.renderTotalsAndChart(
          Object.values(reviewData.updatedByDateTime)
            .reduce((acc, day) => acc + day.total, 0)
            .toLocaleString(),
          Object.values(reviewData.updatedByDateTime).reduce((acc, day) => acc + day.positiveReviews, 0),
          Object.values(reviewData.updatedByDateTime).reduce((acc, day) => acc + day.negativeReviews, 0),
          "TOTAL",
          "POSITIVE",
          "NEGATIVE",
          ["#F67902", "#FFA900"],
          updatedReviewMenu === MenuOption.TimeOfDay,
          data,
        );
      }
    } else {
      content = (
        <div className="loadingContent">
          <Spinner />
        </div>
      );
    }
    return (
      <div className="chartBox">
        <div className="chartHeader">
          <div className="chartHeaderTitle">
            <span>Updated Reviews by </span>
            <ButtonWithOptions
              color={ButtonColor.WHITE}
              size={ButtonSize.SMALL}
              buttonOptions={[
                { title: "Day", name: "updatedReviewMenu_Day", onClick: this.menuSelected },
                { title: "Time of Day", name: "updatedReviewMenu_TimeOfDay", onClick: this.menuSelected },
                { title: "Day of Week", name: "updatedReviewMenu_DayOfWeek", onClick: this.menuSelected },
              ]}
            >
              {TitleForMenuOption(updatedReviewMenu || MenuOption.Day)}
            </ButtonWithOptions>
          </div>
          <div className="spacer" />
          <div className="chartKey">
            <div className="chartKeyItem">
              <span className="chartKeyBox" style={{ background: "#F67902" }} />
              Positive
            </div>
            <div className="chartKeyItem">
              <span className="chartKeyBox" style={{ background: "#FFA900" }} />
              Negative
            </div>
          </div>
        </div>
        <div className="chartContent">{content}</div>
      </div>
    );
  }

  renderTotalsAndChart(
    total: any,
    first: number,
    vs: number,
    totalText: string,
    firstText: string,
    vsText: string,
    colors: string[],
    showingTimeOfDay: boolean,
    data: StackedBarGraphData,
  ) {
    return (
      <div className="TotalsAndChart">
        <div className="totalsColumn">
          <div className="total">
            <div className="number">{total}</div>
            <label>{totalText}</label>
          </div>

          <div className="vs">
            <div className="number" style={{ color: colors[0] }}>
              {first.toLocaleString()}
            </div>
            <label>{firstText}</label>
            <div className="verseLabel">vs.</div>
            <div className="number" style={{ color: colors[1] }}>
              {vs.toLocaleString()}
            </div>
            <label>{vsText}</label>
          </div>
        </div>
        <div className="stackedChart">
          <StackedBarGraph data={data} width={470} height={260} showingTimeOfDay={showingTimeOfDay} />
        </div>
      </div>
    );
  }

  renderAutomation() {
    const { nlpData, automationMenu } = this.state;
    let content;
    if (nlpData) {
      if (nlpData && (!nlpData.total || !nlpData.total.total)) {
        content = <div className="loadingContent ">{BlankState()}</div>;
      } else {
        const data = HandledDataForType(nlpData, automationMenu);
        let automation = "";
        if (nlpData.total.total || nlpData.total.unhandled) {
          automation = Math.round((nlpData.total.handled / (nlpData.total.handled + nlpData.total.unhandled)) * 100).toLocaleString() + "%";
        }
        content = this.renderTotalsAndChart(
          automation,
          nlpData.total.handled,
          nlpData.total.unhandled,
          "AUTOMATION",
          "HANDLED",
          "UNHANDLED",
          ["#00CABE", "#F67902"],
          automationMenu === MenuOption.TimeOfDay,
          data,
        );
      }
    } else {
      content = (
        <div className="loadingContent">
          <Spinner />
        </div>
      );
    }

    return (
      <div className="chartBox ">
        <div className="chartHeader">
          {" "}
          <div className="chartHeaderTitle">
            {" "}
            <span>Automations by </span>
            <ButtonWithOptions
              color={ButtonColor.WHITE}
              size={ButtonSize.SMALL}
              buttonOptions={[
                { title: "Day", name: "automationMenu_Day", onClick: this.menuSelected },
                { title: "Time of Day", name: "automationMenu_TimeOfDay", onClick: this.menuSelected },
                { title: "Day of Week", name: "automationMenu_DayOfWeek", onClick: this.menuSelected },
              ]}
            >
              {TitleForMenuOption(automationMenu)}
            </ButtonWithOptions>
          </div>
          <div className="spacer" />
          <div className="chartKey">
            <div className="chartKeyItem">
              {" "}
              <span className="chartKeyBox" style={{ background: "#00CABE" }} />
              Handled
            </div>{" "}
            <div className="chartKeyItem">
              {" "}
              <span className="chartKeyBox" style={{ background: "#F67902" }} />
              Unhandled
            </div>
          </div>{" "}
        </div>
        <div className="chartContent">{content}</div>
      </div>
    );
  }

  renderUser() {
    const { userData, userMenu } = this.state;
    let content;
    if (userData && userData.total) {
      if (!userData.total.total) {
        content = <div className="loadingContent ">{BlankState()}</div>;
      } else {
        const data = UserDataForType(userData, userMenu);
        content = this.renderTotalsAndChart(
          userData.total.total.toLocaleString(),
          userData.total.newUsers,
          userData.total.returningUsers,
          "TOTAL",
          "NEW",
          "RETURNING",
          ["#FF5B02", "#FFA900"],
          userMenu === MenuOption.TimeOfDay,
          data,
        );
      }
    } else {
      content = (
        <div className="loadingContent">
          <Spinner />
        </div>
      );
    }
    return (
      <div className="chartBox ">
        <div className="chartHeader">
          {" "}
          <div className="chartHeaderTitle">
            {" "}
            <span>Chats by </span>
            <ButtonWithOptions
              color={ButtonColor.WHITE}
              size={ButtonSize.SMALL}
              buttonOptions={[
                { title: "Day", name: "userMenu_Day", onClick: this.menuSelected },
                { title: "Time of Day", name: "userMenu_TimeOfDay", onClick: this.menuSelected },
                { title: "Day of Week", name: "userMenu_DayOfWeek", onClick: this.menuSelected },
              ]}
            >
              {TitleForMenuOption(userMenu)}
            </ButtonWithOptions>
          </div>
          <div className="spacer" />
          <div className="chartKey">
            {" "}
            <div className="chartKeyItem">
              {" "}
              <span className="chartKeyBox" style={{ background: "#FF5B02" }} />
              New
            </div>
            <div className="chartKeyItem">
              {" "}
              <span className="chartKeyBox" style={{ background: "#FFA900" }} />
              Returning
            </div>
          </div>{" "}
        </div>
        <div className="chartContent">{content}</div>
      </div>
    );
  }

  renderQuests() {
    const { questData } = this.state;
    let content, total, viewAll;
    if (questData) {
      if (!questData.total) {
        total = "";
        content = <div className="loadingContent ">{BlankState()}</div>;
      } else {
        const data = QuestData(questData);
        total = questData.total.toLocaleString();
        content = (
          <div className="barAndPieWapper">
            <BarAndPieChart data={data} showTotals={true} width={580} height={200} />
          </div>
        );
        viewAll = (
          <a className="viewAll" onClick={this.props.viewAllQuestsClicked}>
            VIEW ALL
          </a>
        );
      }
    } else {
      content = (
        <div className="loadingContent">
          <Spinner />
        </div>
      );
    }
    return (
      <div className="chartBox ">
        <div className="chartHeader">
          <div className="chartHeaderTitle">
            {" "}
            <span>Quests Visited </span>{" "}
          </div>
          <div className="spacer" />
          <div className="headerTotal">{total}</div>
        </div>
        <div className="chartContent">{content}</div>
        {viewAll}
      </div>
    );
  }

  TopReviewLocations(data: ReviewData): BarAndPieChartData[] {
    const locationData: { [key: number]: { total: number } } = {};

    for (const key in data.byDateTime) {
      if (Object.prototype.hasOwnProperty.call(data.byDateTime, key)) {
        const element = data.byDateTime[key];
        const locationId = element.locationId;

        if (!locationData[locationId]) {
          locationData[locationId] = { total: 0 };
        }
        locationData[locationId].total += element.total;
      }
    }

    const output: BarAndPieChartData[] = Object.keys(locationData).map((locationId) => ({
      title: this.locationNames[locationId] || locationId,
      value: locationData[locationId].total,
    }));

    return output;
  }

  renderTopReviewLocations() {
    const { reviewData, showAllReviews } = this.state;
    let content, total, viewAll;
    if (reviewData) {
      if (!reviewData.byDateTime || Object.keys(reviewData.byDateTime).length === 0) {
        content = <div className="loadingContent ">{BlankState()}</div>;
      } else {
        const data = this.TopReviewLocations(reviewData);
        total = Object.values(reviewData.byDateTime)
          .reduce((acc, day) => acc + day.total, 0)
          .toLocaleString();
        content = (
          <div className="barAndPieWapper">
            <BarAndPieChart data={data} showTotals={true} width={580} height={200} />
          </div>
        );
        viewAll = (
          <button className="viewAll" onClick={() => this.setState({ showAllReviews: true })}>
            VIEW ALL
          </button>
        );
      }
    } else {
      content = (
        <div className="loadingContent">
          <Spinner />
        </div>
      );
    }

    return (
      <div className="chartBox ">
        <div className="chartHeader">
          <div className="chartHeaderTitle">
            <span>Top Review Locations</span>
          </div>
          <div className="spacer" />
          <div className="headerTotal">{total}</div>
        </div>
        <div className="chartContent">{content}</div>
        {viewAll}
      </div>
    );
  }

  renderRatings() {
    const { reviewData } = this.state;
    let content,
      total,
      viewAll,
      averageRating = "";

    if (reviewData && reviewData.byDateTime) {
      if (Object.keys(reviewData.byDateTime).length === 0) {
        content = <div className="loadingContent">{BlankState()}</div>;
      } else {
        const reviews = Object.values(reviewData.byDateTime).flatMap((day) => day.reviews);
        const chartData = transformRatingDataForChart(reviews);
        const totalReviews = reviews.length;
        if (totalReviews > 0) {
          averageRating = (reviews.reduce((acc, review) => acc + review.rating, 0) / totalReviews).toFixed(1);
        }

        content = (
          <div className="barAndPieWapper">
            <BarAndPieChart data={chartData} showTotals={true} width={580} height={200} />
          </div>
        );
        // viewAll = <a className="viewAll" onClick={this.props.viewAllIntentsClicked}>VIEW ALL</a>;
      }
    } else {
      content = (
        <div className="loadingContent">
          <Spinner />
        </div>
      );
    }

    return (
      <div className="chartBox">
        <div className="chartHeader">
          <div className="chartHeaderTitle">
            <span>Rating Summary</span>
          </div>
          <div className="spacer" />
          <div className="headerTotal">Average: {averageRating} ⭐️</div>
        </div>
        <div className="chartContent">{content}</div>
      </div>
    );
  }

  renderTopIntents() {
    const { nlpData } = this.state;
    let content, total, viewAll;
    if (nlpData) {
      if (!nlpData.total || !nlpData.total.total) {
        content = <div className="loadingContent ">{BlankState()}</div>;
      } else {
        const data = TopIntents(nlpData);
        total = nlpData.total.total.toLocaleString();
        content = (
          <div className="barAndPieWapper">
            <BarAndPieChart data={data} showTotals={true} width={580} height={200} />
          </div>
        );
        viewAll = (
          <a className="viewAll" onClick={this.props.viewAllIntentsClicked}>
            VIEW ALL
          </a>
        );
      }
    } else {
      content = (
        <div className="loadingContent">
          <Spinner />
        </div>
      );
    }
    return (
      <div className="chartBox ">
        <div className="chartHeader">
          <div className="chartHeaderTitle">
            {" "}
            <span>Top Intents</span>{" "}
          </div>
          <div className="spacer" />
          <div className="headerTotal">{total}</div>
        </div>
        <div className="chartContent">{content}</div>
        {viewAll}
      </div>
    );
  }

  render() {
    const { showAllReviews } = this.state;

    if (showAllReviews) {
      return <ViewAllReviews />;
    }

    return (
      <div className="BotAnalyticsDashboard">
        {/* <div className="sectionHeader"> Chat Stats  </div> */}
        <div className="chartBoxes topRow">
          {/*{this.renderAutomation()}
          {this.renderUser()}*/}
          {this.renderReviews()}
          {this.renderUpdatedReviews()}
        </div>
        <div className="chartBoxes">
          {/*{this.renderQuests()}
          {this.renderTopIntents()}*/}
          {this.renderRatings()}
          {this.renderTopReviewLocations()}
        </div>
      </div>
    );
  }
}

export default BotAnalyticsDashboard;
